import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, TextArea, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { useToast } from '@/components/toast'
import { api, apiMutate } from '@/lib/api'
import { fmtDateTime } from '@/lib/labels'

type Template = {
  id: string
  name: string
  subject: string | null
  bodyHtml: string | null
  bodyText: string | null
  variables: string[]
  channels: string[]
  updatedAt: string
}

export function Modeles() {
  const toast = useToast()
  const qc = useQueryClient()
  const [edit, setEdit] = useState<Template | null>(null)
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [bodyText, setBodyText] = useState('')

  const list = useQuery({
    queryKey: ['admin-templates'],
    queryFn: () => api<{ items: Template[] }>('/admin/templates'),
  })

  const patch = useMutation({
    mutationFn: () =>
      apiMutate(`/admin/templates/${edit!.id}`, 'PATCH', {
        subject,
        bodyHtml,
        bodyText,
      }),
    onSuccess: () => {
      toast.ok('Modèle enregistré')
      setEdit(null)
      void qc.invalidateQueries({ queryKey: ['admin-templates'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = list.data?.items ?? []

  return (
    <div className="flex flex-col gap-5">
      {list.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Aucun modèle" body="Les modèles de messages seront listés ici." />
      ) : (
        <section className="carte-admin overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-bordure-admin">
                {['Nom', 'Sujet', 'Canaux', 'MAJ', ''].map((h) => (
                  <th key={h || 'a'} className="px-4 py-2.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className="border-b border-bordure-admin last:border-0">
                  <td className="px-4 py-2.5 font-medium">{t.name}</td>
                  <td className="px-4 py-2.5 text-second-admin">{t.subject ?? '—'}</td>
                  <td className="px-4 py-2.5 text-second-admin">
                    {(t.channels ?? []).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-second-admin">{fmtDateTime(t.updatedAt)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <RowActions
                      items={[
                        {
                          label: 'Éditer',
                          onSelect: () => {
                            setEdit(t)
                            setSubject(t.subject ?? '')
                            setBodyHtml(t.bodyHtml ?? '')
                            setBodyText(t.bodyText ?? '')
                          },
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <Modal
        open={Boolean(edit)}
        title={edit ? `Éditer · ${edit.name}` : 'Modèle'}
        size="lg"
        onClose={() => setEdit(null)}
        footer={
          <>
            <BtnGhost onClick={() => setEdit(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={patch.isPending} onClick={() => patch.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {edit?.variables?.length ? (
            <p className="text-xs text-second-admin">
              Variables : {edit.variables.join(', ')}
            </p>
          ) : null}
          <Field label="Sujet">
            <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Field>
          <Field label="Corps HTML / MJML">
            <TextArea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={8} />
          </Field>
          <Field label="Corps texte">
            <TextArea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={4} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
